import React, { useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Box, Breadcrumbs, Button, CircularProgress, Grid, Link, Typography } from '@mui/material';
import { ArrowForwardIos } from '@mui/icons-material';

import { CrystalButtonPopup, ZonesTabInZones, ZoneTabSearch } from 'components';
import { ROUTES } from 'consts';
import { BuildingCreateDialog } from 'dialogs/building';
import { ZoneCreateDialog } from 'dialogs/zone';
import { useQueryValues } from 'hooks/url.hook';
import { useBuildings } from 'pages/buildings/buildings.hook';
import { useActiveZonePage, useZoneFilters } from 'pages/zones/zones.hook';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { getBuildingLocation } from 'utils';

interface ZonesLayoutProps {
  children?: React.ReactNode;
  hideCreateButton?: boolean;
}

interface ActionRegistry {
  downloadDecals?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ZonesLayout = ({ children, hideCreateButton = false }: ZonesLayoutProps) => {
  const [loading, buildings] = useBuildings();
  const { building: buildingIdQuery } = useQueryValues(['building']);
  const { orgSlug, isAdmin } = useAppSelector(selectCurrentUserInfo);
  const { tabKey } = useActiveZonePage();
  const navigate = useNavigate();

  const [visibleBuildingCreateDialog, setVisibleBuildingCreateDialog] = useState(false);
  const [visibleZoneCreateDialog, setVisibleZoneCreateDialog] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  const buildingId = buildingIdQuery ? Number(buildingIdQuery) : null;
  const building = buildingId ? buildings.find((b) => b.id === buildingId) : undefined;
  const hasDownloadDecalItems = selected.length > 0;

  const {
    loading: zoneLoading,
    zones,
    filteredZones,
    filters,
    setSearch,
    setFilters,
    counts,
  } = useZoneFilters({ tabKey, buildingId });

  const actionRegistry = useRef<ActionRegistry>({});

  const runAction = (name: keyof ActionRegistry) => {
    actionRegistry.current[name]?.();
  };

  const pageMenuItems = [
    { id: 1, label: 'Building', onClick: () => setVisibleBuildingCreateDialog(true) },
    { id: 2, label: 'Zone', onClick: () => setVisibleZoneCreateDialog(true) },
  ];

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Header */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Breadcrumbs
                separator={
                  <Box px={1.5}>
                    <ArrowForwardIos sx={{ fontSize: 10 }} />
                  </Box>
                }
              >
                <Link
                  component="button"
                  variant="subtitle2"
                  color="textSecondary"
                  onClick={() => navigate(`/${orgSlug}/${ROUTES.ZONES}`)}
                >
                  Zones
                </Link>

                {loading || (buildingId && !building) ? (
                  <CircularProgress size={16} sx={{ mt: 0.5 }} />
                ) : (
                  <Typography variant="subtitle2" color="primary">
                    {building ? getBuildingLocation(building) : 'All'}
                  </Typography>
                )}
              </Breadcrumbs>

              {!hideCreateButton && isAdmin && <CrystalButtonPopup items={pageMenuItems} label="" />}
            </Box>
          </Grid>

          {/* Search (now owns filtering) */}
          <Grid item xs={12} sm={7}>
            <ZoneTabSearch filters={filters} zones={zones} onSearchChange={setSearch} onFiltersChange={setFilters} />
          </Grid>

          {/* Actions */}
          <Grid item xs={12} sm={5} display="flex" justifyContent="flex-end">
            <Button
              sx={{
                maxHeight: 50,
                width: { xs: '100%', sm: 'auto' },
                display: { xs: 'none', sm: 'block' },
                borderRadius: '4px',
              }}
              disabled={!hasDownloadDecalItems}
              variant="contained"
              onClick={() => runAction('downloadDecals')}
            >
              Download All Decals
            </Button>
          </Grid>

          {/* Tabs + Content */}
          <Grid item xs={12}>
            <ZonesTabInZones counts={counts} loading={zoneLoading} />
            <Outlet
              context={{
                loading: zoneLoading,
                selected,
                setSelected,
                registerAction: (name: keyof ActionRegistry, fn: any) => {
                  actionRegistry.current[name] = fn;
                },
                runAction,
                zones: filteredZones,
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Dialogs */}
      <BuildingCreateDialog visible={visibleBuildingCreateDialog} setVisible={setVisibleBuildingCreateDialog} />

      <ZoneCreateDialog
        visible={visibleZoneCreateDialog}
        setVisible={setVisibleZoneCreateDialog}
        buildingId={buildingId}
      />
    </>
  );
};
